/**
 * プレイ中の「おはし」ボタンと、選ぶモードの案内。
 *
 * Web版では何も出さない（assist.enabled が false）。
 *
 * 「こぼれそう！」が続いたとき、おはしを持っていなければ、ボタンの右に「▶ 広告でおはし」を少しのあいだ出す
 * （1プレイ1回まで。見るかどうかは本人が決める。見終わったらそのまま料理を選ぶモードに入る。2026-10-11）。
 */

import { GameState } from '../core/GameState';
import type { GameContext } from '../game/GameContext';
import type { ShopModal } from './ShopModal';
import { track } from '../meta/telemetry';
import { isStreamerMode } from './streamerMode';

/** 「広告でおはし」を出しておく時間。 */
const RESCUE_SHOW_MS = 8000;

export class AssistBar {
  private readonly button: HTMLButtonElement;
  private readonly hint: HTMLElement;
  private readonly hintText: HTMLElement;
  private readonly confirmRow: HTMLElement;
  private picking = false;
  private readonly rescue: HTMLButtonElement;
  private rescueTimer = 0;

  constructor(
    private readonly context: GameContext,
    private readonly shop: ShopModal,
    root: ParentNode = document,
  ) {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'btn assist-item';
    this.button.dataset.ui = 'item';
    this.button.addEventListener('click', () => this.onClick());

    // 選ぶモードの案内と、料理を選んだあとの小さな確認「この料理を消す？ はい／やめる」。
    // タップは選ぶだけで、「はい」を押すまでおはしは減らない（毎回確かめる。2026-10-05）
    this.hint = document.createElement('div');
    this.hint.className = 'pick-hint';
    this.hint.dataset.visible = 'false';
    this.hintText = document.createElement('p');
    this.hintText.className = 'pick-hint__text';
    this.hintText.setAttribute('role', 'status');
    this.hintText.textContent = '消したい料理をタップ';
    this.confirmRow = document.createElement('div');
    this.confirmRow.className = 'pick-hint__actions';
    this.confirmRow.hidden = true;
    const yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'btn btn--primary';
    yes.textContent = 'はい';
    yes.dataset.ui = 'pick-confirm';
    yes.setAttribute('aria-label', 'はい、この料理を消す（おはしを1本使う）');
    yes.addEventListener('click', () => context.bus.emit('command:pickConfirm'));
    const no = document.createElement('button');
    no.type = 'button';
    no.className = 'btn';
    no.textContent = 'やめる';
    no.dataset.ui = 'pick-cancel';
    no.setAttribute('aria-label', 'やめる（おはしは使わない）');
    no.addEventListener('click', () => context.bus.emit('command:pickCancel'));
    this.confirmRow.append(yes, no);
    this.hint.append(this.hintText, this.confirmRow);

    this.rescue = document.createElement('button');
    this.rescue.type = 'button';
    this.rescue.className = 'btn assist-rescue';
    this.rescue.dataset.ui = 'rescue';
    this.rescue.hidden = true;
    this.rescue.textContent = '▶ 広告でおはし';
    this.rescue.setAttribute('aria-label', '広告を見て、おはしを1本もらう（料理を1つ消せる）');
    this.rescue.addEventListener('click', () => void this.onRescue());

    if (context.assist.enabled) {
      // 盤面の左下（器の外側）に置く。盤面の下の行だと、iPhone SE（高さ667px）で画面の外に出て
      // プレイ中にスクロールが要った（2026-10-04）
      this.button.classList.add('assist-item--board');
      root.querySelector('.stage-frame')?.append(this.button);
      root.querySelector('.stage-frame')?.append(this.hint);
      root.querySelector('.stage-frame')?.append(this.rescue);
    }

    context.bus.on('board:danger', ({ active }) => {
      if (active) this.maybeOfferRescue();
    });
    context.state.onChange((to) => {
      if (to !== GameState.PLAYING) this.hideRescue();
    });

    context.bus.on('pick:mode', ({ active }) => {
      this.picking = active;
      this.hint.dataset.visible = String(active);
      if (active) this.showSelection(null);
      this.render();
    });
    context.bus.on('pick:select', ({ name }) => this.showSelection(name));
    context.assist.onChange(() => this.render());
    context.state.onChange(() => this.render());
    this.render();
  }

  /** 「こぼれそう！」が続いた：条件がそろえば「広告でおはし」を少しのあいだ出す。 */
  private maybeOfferRescue(): void {
    const { assist, state } = this.context;
    if (!state.is(GameState.PLAYING) || this.picking || isStreamerMode()) return;
    if (!assist.canOfferRescue()) return;
    assist.markRescueOffered();
    track('ad_offer', { detail: 'rescue' });
    this.rescue.hidden = false;
    window.clearTimeout(this.rescueTimer);
    this.rescueTimer = window.setTimeout(() => this.hideRescue(), RESCUE_SHOW_MS);
  }

  private hideRescue(): void {
    window.clearTimeout(this.rescueTimer);
    this.rescue.hidden = true;
  }

  /** 広告を見ているあいだはゲームを止め、見終わったら料理を選ぶモードに入る。 */
  private async onRescue(): Promise<void> {
    const { assist, bus, state } = this.context;
    this.hideRescue();
    if (state.is(GameState.PLAYING)) bus.emit('command:pause');
    const ok = await assist.earnItemByAd('rescue');
    if (state.is(GameState.PAUSED)) bus.emit('command:resume');
    if (ok && state.is(GameState.PLAYING) && assist.items > 0 && assist.canUseItemThisRun()) {
      bus.emit('command:pickStart');
    }
  }

  private onClick(): void {
    const { assist, bus, state } = this.context;
    if (this.picking) {
      bus.emit('command:pickCancel');
      return;
    }
    if (!assist.canUseItemThisRun() && state.is(GameState.PLAYING)) {
      // 1プレイ3本まで。押しても何も起きないのは不親切なので、ボタンの字で知らせる（render）
      return;
    }
    if (assist.items > 0 && state.is(GameState.PLAYING)) {
      bus.emit('command:pickStart');
      return;
    }
    // 持っていない、またはプレイ中でない → ショップ（プレイ中なら一時停止してから）
    if (state.is(GameState.PLAYING)) bus.emit('command:pause');
    this.shop.open('ohashi_button');
  }

  private showSelection(name: string | null): void {
    this.hint.dataset.selected = String(name !== null);
    this.confirmRow.hidden = name === null;
    this.hintText.textContent = name === null ? '消したい料理をタップ' : `「${name}」を消す？`;
  }

  private render(): void {
    const { assist } = this.context;
    this.button.hidden = !assist.enabled;
    this.button.dataset.active = String(this.picking);
    const limit = !assist.canUseItemThisRun();
    this.button.disabled = limit && !this.picking;
    // 盤面の左下の角に収まるよう短く（幅が広いと、左の列をねらった下のほうのタップがボタンに当たっていた）。
    // 詳しい言い方は aria-label と title に
    this.button.textContent = this.picking ? 'やめる' : limit ? '🥢 済' : `🥢 ×${assist.items}`;
    this.button.title = limit && !this.picking ? 'おはしは1プレイ3本まで。このプレイはここまでです' : '';
    this.button.setAttribute(
      'aria-label',
      this.picking
        ? 'おはしで選ぶのをやめる'
        : limit
          ? 'おはしは1プレイ3本まで。このプレイはここまでです'
          : assist.items > 0
          ? `おはしで料理を1つ消す（残り${assist.items}本）`
          : 'おはしがありません。ショップを開く',
    );
  }
}
